import { UTCDate } from 'types';
import { ValidationSeveritySetting } from 'validation';
import { Features, ValidationRules } from 'controls';
import { firstNames$, middleNames$ } from './dict';
import { RequestFeatures } from './features';
import { RequestFeaturesV2 } from './features2';
import { BirthPlaceDictionary } from './dictionaries/birthplace.dictionary';

class UserValidationRules {
  private static readonly dipServicePassportSeries = ['10', '11', '12', '20', '21', '22'];
  private static readonly commonPassport5yearsSeries = ['29', '50', '51', '52', '60', '61', '62', '63', '64', '65', '66', '67', '70', '40', '41', '42', '43', '44', '45', '46', '99'];
  private static readonly commonPassport10yearsSeries = ['53', '55', '71', '72', '73', '75', '76'];
  private static readonly identityDocumentExpirationDaysAfterNameChange = 90;

  static first_name_gender(params: any) {
    const genderPath = params.gender.split('.');
    const result = function (value: string, root: any, parent: any): any {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (typeof value !== 'string') {
        return 'FORMAT_ERROR';
      }
      const genderValue = ValidationRules.fetchNode(root, parent, genderPath);
      if (genderValue === undefined || genderValue === null || genderValue === '') {
        return;
      }
      if (typeof genderValue !== 'string') {
        return 'FORMAT_ERROR';
      }
      const found = ValidationRules.checkExists(firstNames$, (x) => x.value === value && x.gender.val() !== genderValue);
      if (found) {
        return 'GENDER_MISMATCH';
      }
    };
    return result;
  }

  static middle_name_gender(params: any) {
    const genderPath = params.gender.split('.');
    const result = function (value: string, root: any, parent: any): any {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (typeof value !== 'string') {
        return 'FORMAT_ERROR';
      }
      const genderValue = ValidationRules.fetchNode(root, parent, genderPath);
      if (genderValue === undefined || genderValue === null || genderValue === '') {
        return;
      }
      if (typeof genderValue !== 'string') {
        return 'FORMAT_ERROR';
      }
      const found = ValidationRules.checkExists(middleNames$, (x) => x.value === value && x.gender.val() !== genderValue);
      const find_suffix = function () {
        switch (genderValue) {
          case '0':
            if (['ВНА', 'КЗЫ', 'ЫЗЫ', 'ЧНА', 'ОВА', 'ЕВА'].some((x) => value.endsWith(x))) {
              return true;
            }
            break;
          case '1':
            if (['ВИЧ', 'ОГЛЫ', 'ОГЛУ', 'ЛЬИЧ', 'МИЧ'].some((x) => value.endsWith(x))) {
              return true;
            }
            break;
        }
        return false;
      };
      if (found || find_suffix()) {
        return 'GENDER_MISMATCH';
      }
    };
    return result;
  }

  static middle_name_stubs(params: any) {
    const middleNamePath = params.middleName.split('.');
    return function (value: string, root: any, parent: any): any {
      if (value === undefined || value === null || value === '') {
        return;
      }
      const middleNameValue = ValidationRules.fetchNode(root, parent, middleNamePath);
      if (middleNameValue === undefined || middleNameValue === null || middleNameValue === '') {
        return;
      }
      if (typeof middleNameValue.value !== 'string') {
        return 'FORMAT_ERROR';
      }
      if (!new RegExp(Validation.reMiddleNameStubs).test(middleNameValue)) {
        return 'MIDDLE_NAME_STUBS';
      }
    };
  }

  static birthplace_mismatch(params: any) {
    const birthDatePath = params.birthDate.split('.');
    const birthPlacePath = params.birthPlace.split('.');
    const result = function (value: string, root: any, parent: any): any {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (typeof value !== 'string') {
        return 'FORMAT_ERROR';
      }
      const birthDateValue = ValidationRules.fetchNode(root, parent, birthDatePath);
      if (birthDateValue === undefined || birthDateValue === null || birthDateValue === '') {
        return;
      }
      const birthPlaceValue = ValidationRules.fetchNode(root, parent, birthPlacePath);
      if (birthPlaceValue === undefined || birthPlaceValue === null || birthPlaceValue === '') {
        return;
      }
      const birthdayDn = UTCDate.iso(birthDateValue.value, false);
      const birthdayUp = UTCDate.iso(birthDateValue.value, true);
      const birthPlacesByBirthDate = BirthPlaceDictionary.foreignBirthPlaces.filter(
        (birthplace) =>
          birthdayDn === null ||
          birthdayUp === null ||
          ((birthplace.from === null || birthdayUp >= birthplace.from) && (birthplace.to === null || birthdayDn < birthplace.to)),
      );
      const foundBirthPlaces = birthPlacesByBirthDate.find(
        (birthplace) => birthplace.russian === birthPlaceValue || birthplace.latin === birthPlaceValue,
      );
      if (!foundBirthPlaces) {
        return 'BIRTHPLACE_MISMATCH';
      }
    };
    return result;
  }

  static incorrect_country_name(params: any) {
    const countryNamePath = params.countryName.split('.');

    return function (value: string, root: any, parent: any): any {
      if (value === undefined || value === null || value === '') {
        return;
      }
      const name = ValidationRules.fetchNode(root, parent, countryNamePath);
      if (!name) {
        return;
      }
      if (new RegExp(Validation.reCorrectRussiaName).test(name)) {
        return 'INCORRECT_COUNTRY_NAME';
      }
    };
  }

  static birthplace_insufficient(params: any) {
    const birthplacePath = params.birthplace.split('.');

    return function (value: string, root: any, parent: any): any {
      if (value === undefined || value === null || value === '') {
        return;
      }
      const birthplace = ValidationRules.fetchNode(root, parent, birthplacePath);
      if (!birthplace) {
        return;
      }
      if (new RegExp(Validation.reInsufficientBirthPlaceName).test(birthplace)) {
        return 'BIRTHPLACE_INSUFFICIENT';
      }
    };
  }

  static restrictions_for_common_passsport_type(params: any) {
    const passportTypePath = [params.passportType];
    const result = function (value: string, root: any, parent: any): any {
      const passportTypeValue = ValidationRules.fetchNode(root, parent, passportTypePath)?.value;
      const isPassportDipService = passportTypeValue === '2' || passportTypeValue === '3';
      if ((value === undefined || value === null || value === '') && !isPassportDipService) {
        return 'REQUIRED';
      }
      if (typeof value !== 'string' && !isPassportDipService) {
        return 'FORMAT_ERROR';
      }
    };
    return result;
  }

  static foreign_passport_series() {
    const result = function (value: string, root: any): any {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (typeof value !== 'string') {
        return 'FORMAT_ERROR';
      }
      const passportTypePath = ['desiredForeignPassportType'];
      const passportTypeValue = ValidationRules.fetchNode(root, parent, passportTypePath)?.value;
      const isPassportDipService = passportTypeValue === '2' || passportTypeValue === '3';
      if (UserValidationRules.dipServicePassportSeries.includes(value) && !isPassportDipService) {
        return 'FOREIGN_PASSPORT_NOT_COMMON';
      }
      const knownPassportSeries = [
        ...UserValidationRules.dipServicePassportSeries,
        ...UserValidationRules.commonPassport5yearsSeries,
        ...UserValidationRules.commonPassport10yearsSeries,
      ];
      if (!knownPassportSeries.includes(value)) {
        return 'FOREIGN_PASSPORT_UNKNOWN_SERIES';
      }
    };
    return result;
  }

  static foreign_passport_issued(params: any) {
    const seriesPath = params.series.split('.');
    const result = function (value: string, root: any, parent: any): any {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (typeof value !== 'string') {
        return 'FORMAT_ERROR';
      }
      const obj = UTCDate.iso(value);
      if (!obj) {
        return 'FORMAT_ERROR';
      }
      const seriesValue = ValidationRules.fetchNode(root, parent, seriesPath);
      if (seriesValue === undefined || seriesValue === null || seriesValue === '') {
        return;
      }
      if (typeof seriesValue !== 'string') {
        return 'FORMAT_ERROR';
      }
      const years = [...UserValidationRules.commonPassport5yearsSeries, ...UserValidationRules.dipServicePassportSeries].includes(
        seriesValue,
      )
        ? 5
        : 10;
      if (obj.addYears(years) < ValidationRules.today(root)) {
        return 'FOREIGN_PASSPORT_EXPIRED';
      }
    };
    return result;
  }

  static foreign_passport_canceled() {
    const result = function (value: any, root: any): any {
      if (!value) {
        return;
      }
      let falsed = 0;
      let trued = 0;
      if (value.p1) {
        if (String(value.p1.mustBeCanceled) === 'false') {
          falsed++;
        }
        if (String(value.p1.mustBeCanceled) === 'true') {
          trued++;
        }
      }
      if (value.p2) {
        if (String(value.p2.mustBeCanceled) === 'false') {
          falsed++;
        }
        if (String(value.p2.mustBeCanceled) === 'true') {
          trued++;
        }
      }
      if (falsed && !trued && (root.cause.value !== '3' || (value.p1 && value.p2))) {
        return 'FOREIGN_PASSPORT_NOT_CANCELED';
      }
    };
    return result;
  }

  static foreign_passport_lost() {
    const result = function (value: any, root: any): any {
      if (!value) {
        return;
      }
      if (root.cause.value === '3') {
        let falsed = 0;
        let trued = 0;
        if (value.p1) {
          if (String(value.p1.lost) === 'false') {
            falsed++;
          }
          if (String(value.p1.lost) === 'true') {
            trued++;
          }
        }
        if (value.p2) {
          if (String(value.p2.lost) === 'false') {
            falsed++;
          }
          if (String(value.p2.lost) === 'true') {
            trued++;
          }
        }
        if (falsed && !trued && value.p1 && value.p2) {
          return 'FOREIGN_PASSPORT_NOT_LOST';
        }
      }
    };
    return result;
  }

  static old_fio(params: any) {
    const fioPath = params.fio.split('.');
    const result = function (values: any[], root: any, parent: any): any {
      if (!values || !values.length) {
        return;
      }
      values = values.slice();
      values.sort((a, b) => {
        if (a.year.value > b.year.value) {
          return 1;
        }
        if (a.year.value < b.year.value) {
          return -1;
        }
        return 0;
      });
      values.reverse();
      const fioValue = ValidationRules.fetchNode(root, parent, fioPath);
      let fioTotal = 0;
      let fioMatch = 0;
      for (const value of values) {
        if (value.year.value !== values[0].year.value) {
          break; // found too old record, stop
        }
        ++fioTotal;
        if (
          fioValue.lastName === value.lastName &&
          fioValue.firstName === value.firstName &&
          fioValue.middleName.value === value.middleName.value
        ) {
          ++fioMatch;
        }
      }
      if (fioMatch === fioTotal) {
        return 'MATCH';
      }
    };
    return result;
  }

  static any_job() {
    const result = function (value: any[], root: any): any {
      const passportTypePath = ['desiredForeignPassportType'];
      const passportTypeValue = ValidationRules.fetchNode(root, parent, passportTypePath)?.value;
      const isPassportDipService = passportTypeValue === '2' || passportTypeValue === '3';
      if (!value) {
        return;
      }
      if (value.length === 0 && !isPassportDipService) {
        return 'JOB_LIST_IS_EMPTY';
      }
    };
    return result;
  }

  static job_break() {
    const result = function (value: any[], root: any): any {
      if (!value) {
        return;
      }
      const ranges = new Array<{ from: UTCDate; to: UTCDate | null }>();
      for (const job of value) {
        const from = UTCDate.iso(job.from.value);
        if (from === null) {
          return 'FORMAT_ERROR';
        }
        const to = job.to.value !== null ? UTCDate.iso(job.to.value) : null;
        if (job.to.value !== null && to === null) {
          return 'FORMAT_ERROR';
        }
        ranges.push({ from: from, to: to });
      }
      ranges.sort((a, b) => a.from.valueOf() - b.from.valueOf());
      let start = ValidationRules.today(root);
      start = start.addDays(1 - start.getDate());
      start = start.addYears(-10);
      const diff = function (from: UTCDate, to: UTCDate) {
        from = from < start ? start : from;
        to = to < start ? start : to;
        return (to.getFullYear() - from.getFullYear()) * 12 + to.getMonth() - from.getMonth();
      };
      let diffSumm = 0;
      let currTo: UTCDate | null = null;
      let firstBreak: { from: UTCDate; to: UTCDate | null } | null = null;
      const passportTypePath = ['desiredForeignPassportType'];
      const passportTypeValue = ValidationRules.fetchNode(root, parent, passportTypePath)?.value;
      const isPassportDipService = passportTypeValue === '2' || passportTypeValue === '3';
      for (const range of ranges) {
        const edgeForCompare = currTo === null ? start : currTo.addMonths(3);
        if (edgeForCompare < range.from) {
          const edgeForBreak = currTo === null ? start : currTo;
          diffSumm += diff(edgeForBreak, range.from);
          firstBreak = firstBreak || { from: edgeForBreak, to: range.from };
        }
        if (currTo === null || range.to === null || currTo < range.to) {
          currTo = range.to;
        }
        if (currTo === null) {
          break;
        }
      }
      if (currTo !== null) {
        diffSumm += diff(currTo, ValidationRules.today(root)) || 1;
        firstBreak = firstBreak || { from: currTo, to: null };
      }
      if (firstBreak !== null && !isPassportDipService) {
        return JSON.stringify({ type: 'JOB', total: 120, error: diffSumm, break: firstBreak });
      }
      if (firstBreak !== null && isPassportDipService) {
        return 'WARN_FORMAT';
      }
    };
    return result;
  }

  static russian_passport_issued(params: any) {
    const birthDatePath = params.birthDate.split('.');
    const result = function (value: string, root: any, parent: any): any {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (typeof value !== 'string') {
        return 'FORMAT_ERROR';
      }
      const obj = UTCDate.iso(value);
      if (!obj) {
        return 'FORMAT_ERROR';
      }
      const birthDateValue = ValidationRules.fetchNode(root, parent, birthDatePath);
      if (birthDateValue === undefined || birthDateValue === null || birthDateValue === '') {
        return;
      }
      if (typeof birthDateValue !== 'string') {
        return 'FORMAT_ERROR';
      }
      const birthDateObjMin = UTCDate.iso(birthDateValue);
      const birthDateObjMax = UTCDate.iso(birthDateValue, true);
      if (!birthDateObjMin || !birthDateObjMax) {
        return 'FORMAT_ERROR';
      }
      if (obj < birthDateObjMin) {
        return 'NOT_ALLOWED_VALUE';
      }
      if (
        obj < ValidationRules.offsetDate(birthDateObjMin, 20, 0, 0, false) &&
        ValidationRules.offsetDate(birthDateObjMax, 20, 0, 90, true) < ValidationRules.today(root)
      ) {
        return 'RUSSIAN_PASSPORT_EXPIRED_20';
      }
      if (
        obj < ValidationRules.offsetDate(birthDateObjMin, 45, 0, 0, false) &&
        ValidationRules.offsetDate(birthDateObjMax, 45, 0, 90, true) < ValidationRules.today(root)
      ) {
        return 'RUSSIAN_PASSPORT_EXPIRED_45';
      }
    };
    return result;
  }

  static identity_document_mismatched(params: any) {
    const nameChangesPath = params.nameChanges.split('.');
    const result = function (value: string, root: any, parent: any): any {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (typeof value !== 'string') {
        return 'FORMAT_ERROR';
      }
      const obj = UTCDate.iso(value);
      if (!obj) {
        return 'FORMAT_ERROR';
      }
      const nameChangesValue = ValidationRules.fetchNode(root, parent, nameChangesPath) as any[];
      if (!value) {
        return;
      }
      for (const nameChangeValue of nameChangesValue) {
        if (nameChangeValue.year.value) {
          const nameChangeObj = UTCDate.iso(nameChangeValue.year.value);
          const todayObj = UTCDate.today(new Date());
          if (!nameChangeObj) {
            return 'FORMAT_ERROR';
          }

          // Если со смены ФИО прошло более 90 дней, и дата выдачи документа меньше даты смены ФИО, то документ невалиден
          if (
            !nameChangeObj.isWithinNDays(todayObj, UserValidationRules.identityDocumentExpirationDaysAfterNameChange) &&
            !obj.equals(nameChangeObj) &&
            obj.isBefore(nameChangeObj)
          ) {
            return 'IDENTITY_DOCUMENT_MISMATCH';
          }
        }
      }
    };
    return result;
  }

  static birthdate_certificate_mismatched(params: any) {
    const nameChangesPath = params.nameChanges.split('.');
    const result = function (value: string, root: any, parent: any): any {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (typeof value !== 'string') {
        return 'FORMAT_ERROR';
      }
      const obj = UTCDate.iso(value);
      if (!obj) {
        return 'FORMAT_ERROR';
      }
      const nameChangesValue = ValidationRules.fetchNode(root, parent, nameChangesPath) as any[];
      if (!value) {
        return;
      }
      for (const nameChangeValue of nameChangesValue) {
        if (nameChangeValue.year.value) {
          const nameChangeObj = UTCDate.iso(nameChangeValue.year.value);
          const todayObj = UTCDate.today(new Date());
          if (!nameChangeObj) {
            return 'FORMAT_ERROR';
          }
          // Действительность свидетельства о рождении не зависит от смены ФИО, но нужно предупреждение
          if (
            !nameChangeObj.isWithinNDays(todayObj, UserValidationRules.identityDocumentExpirationDaysAfterNameChange) &&
            !obj.equals(nameChangeObj) &&
            obj.isBefore(nameChangeObj)
          ) {
            return 'BIRTHPLACE_CERT_WARN';
          }
        }
      }
    };
    return result;
  }

  static applicant_identity_docs() {
    const result = function (value: any, root: any): any {
      if (!value) {
        return;
      }
      if (value.type.value === '3') {
        const rd = UTCDate.iso(root.receptionDate.value);
        const bd = UTCDate.iso(root.applicant.personIdentity.birthDate.value);
        if (!rd || !bd || bd.addYears(14) <= rd) {
          return;
        }
      }
      if (value.type.value === '1') {
        const rd = UTCDate.iso(root.receptionDate.value);
        const bd = UTCDate.iso(root.applicant.personIdentity.birthDate.value);
        if (rd && bd && bd.addYears(14) > rd) {
          return;
        }
      }
      const passportTypePath = ['desiredForeignPassportType'];
      const passportTypeValue = ValidationRules.fetchNode(root, parent, passportTypePath)?.value;
      const isPassportDipService = passportTypeValue === '2' || passportTypeValue === '3';
      if (isPassportDipService) {
        return;
      }
      let homeland = true;
      if (root.mobileOptions && root.mobileOptions.department !== '07701' && root.mobileOptions.department !== '03901') {
        homeland = false;
      }
      if (
        root.ambulanceOptions &&
        root.ambulanceOptions.departmentLong !== '200007701' &&
        root.ambulanceOptions.departmentLong !== '200003901'
      ) {
        homeland = false;
      }
      if (
        root.stationaryOptions &&
        root.stationaryOptions.departmentLong !== '200007701' &&
        root.stationaryOptions.departmentLong !== '200003901'
      ) {
        homeland = false;
      }
      if (homeland) {
        return 'APPLICANT_IDENITY_NOT_CONFIRMED';
      }
      if (root.applicant.activeFPassports.p1 && String(root.applicant.activeFPassports.p1.lost) !== 'true') {
        return;
      }
      if (root.applicant.activeFPassports.p2 && String(root.applicant.activeFPassports.p2.lost) !== 'true') {
        return;
      }
      if (root.citizenshipCertificate?.active || root.presidentEdict || root.citizenshipDecision) {
        return;
      }
      let reception = true;
      if (root.mobileOptions) {
        reception = false;
      }
      if (reception) {
        return 'APPLICANT_IDENITY_NOT_CONFIRMED_RKZU';
      }
      if (value.type.value === '5') {
        return;
      }
      return 'APPLICANT_IDENITY_NOT_CONFIRMED_RKZU_SITE';
    };
    return result;
  }
}

class Validation {
  private static readonly reCyrillicFullNameWeak = "^[А-ЯЁ\\sIVXLCDM'.\\-()]+$";
  private static readonly reCyrillicFullNameStrict = "^[А-ЯЁ\\s'.\\-()]+$";
  private static readonly reLatinFullName = "^[A-Z\\s'.()\\-]+$";
  private static readonly reNameChangePartWeak = '^[А-ЯЁIVXLCDM\\d\\s()?."#№:\\-+/\'*]+$';
  private static readonly reNameChangePartStrict = '^[А-ЯЁ\\s()?."#№:\\-+/\'*]+$';
  private static readonly reNameChangeLocation = '^[A-ZА-ЯЁ\\d\\s()?."#№:\\-+/\'*]+$';
  private static readonly reCyrillicBirthPlace = '^[А-ЯЁ\\d\\s().",№:;\\-\']+$';
  static readonly reInsufficientBirthPlaceName = '(РОСИЯ|РОССИЯ|РФ|РОССИЙСКАЯ ИМПЕРИЯ|РОССИЙСКАЯ ФЕДЕРАЦИЯ)';
  static readonly reCorrectRussiaName = '^(РОСИЯ|РОССИЯ|РФ|РОССИЙСКАЯ ИМПЕРИЯ)+$';
  static readonly reMiddleNameStubs = '[^(\\.+|\\-+)]';
  private static readonly reLatinBirthPlace = "^[A-Z\\d\\s().,\\-']+$";
  private static readonly reAddressPartName = '^[А-ЯЁIVXLCDM\\d\\s()?.",#№:;\\-/\'*]+$';
  private static readonly rePhone = '^[А-ЯЁA-Z\\d\\s()\\-.+,;]+$';
  private static readonly reEmail = '^[А-ЯЁA-Z<>"\\d\\s\\[\\]/^.|?*+\\-(){}$,;:_\'`!№#%=@~«»]+$';
  private static readonly reCountry = "^[А-ЯЁ,()\\s\\-'`]+$";
  private static readonly reDigit = '^[0-9]+$';
  private static readonly reSpecial0 = '^[А-ЯЁ\\d\\s()?.",#№:;\\-+/\'*]+$';
  private static readonly reSpecial1 = '^[А-ЯЁ\\d\\s()\\-/\'.",?№:+]+$';
  private static readonly reSpecial2 = '^[A-ZА-ЯЁ\\d\\s()\\-/\'.",?№:+]+$';
  private static readonly reEmployer = '^[a-zA-Zа-яА-ЯёЁ\\d\\s()!?.",#№:;\\-+/\'*<>&]+$';
  private static readonly reEmployerCyrillic = '^[а-яА-ЯёЁ\\d\\s()?.",#№:;\\-+/\'*<>&]+$';
  private static readonly reReceptionUser = '^[а-яА-ЯЁ\\d\\s()\\-/\'.",?№:+]+$';
  private static readonly reDutyOrganization = '^[a-zа-яA-ZА-ЯёЁ\\d\\s()?.",#№:;\\-+/\'*]+$';
  private static readonly reBirthCertificateSeries = "^[A-ZА-ЯЁ\\d\\-.']+$";
  private static readonly reAnyDocumentSeries = "^[A-ZА-ЯЁ\\d\\-.']+$";
  private static readonly reAnyDocumentNumber = '^[A-ZА-ЯЁ\\d\\s\\-./\\\\:]+$';
  private static readonly reAnyDocumentIssuer = '^[A-Zа-яА-ЯёЁ\\d\\s()?.",#№:;\\-+/\'*]+$';
  private static readonly reCommon = '^[A-ZА-ЯЁ\\d\\s()?.",#№:;\\-+/\'*]+$';
  private static readonly reExtended = '^[A-ZА-ЯЁ\\d\\s()?.",#№:;\\-+/\'*!@$%^&_=~{}\\[\\]|<>\\\\]+$';
  private static readonly reOfficerPost = '^[а-яёА-ЯЁ\\d\\s()\\-/\'.",?№:+]+$';

  static readonly rules = {
    utc_date: ValidationRules.utc_date,
    max_length_sum: ValidationRules.max_length_sum,
    for_warning: ValidationRules.for_warning,
    like_ex: ValidationRules.like_ex,
    email: ValidationRules.email,
    first_name_gender: UserValidationRules.first_name_gender,
    middle_name_gender: UserValidationRules.middle_name_gender,
    restrictions_for_common_passsport_type: UserValidationRules.restrictions_for_common_passsport_type,
    foreign_passport_series: UserValidationRules.foreign_passport_series,
    foreign_passport_issued: UserValidationRules.foreign_passport_issued,
    foreign_passport_canceled: UserValidationRules.foreign_passport_canceled,
    foreign_passport_lost: UserValidationRules.foreign_passport_lost,
    old_fio: UserValidationRules.old_fio,
    any_job: UserValidationRules.any_job,
    job_break: UserValidationRules.job_break,
    russian_passport_issued: UserValidationRules.russian_passport_issued,
    identity_document_mismatched: UserValidationRules.identity_document_mismatched,
    birthdate_certificate_mismatched: UserValidationRules.birthdate_certificate_mismatched,
    applicant_identity_docs: UserValidationRules.applicant_identity_docs,
    birthplace_mismatch: UserValidationRules.birthplace_mismatch,
    birthplace_insufficient: UserValidationRules.birthplace_insufficient,
    incorrect_country_name: UserValidationRules.incorrect_country_name,
    middle_name_stubs: UserValidationRules.middle_name_stubs,
  };

  static readonly warnings: Array<ValidationSeveritySetting> = [
    { path: 'applicant.personIdentity.fullName.firstName', code: 'GENDER_MISMATCH' },
    { path: 'applicant.personIdentity.fullName.middleName.value', code: 'GENDER_MISMATCH' },
    { path: 'representative.personIdentity.fullName.firstName', code: 'GENDER_MISMATCH' },
    { path: 'representative.personIdentity.fullName.middleName.value', code: 'GENDER_MISMATCH' },
    { path: 'representative.trustRecipient.personIdentity.fullName.firstName', code: 'GENDER_MISMATCH' },
    { path: 'representative.trustRecipient.personIdentity.fullName.middleName.value', code: 'GENDER_MISMATCH' },
    { path: 'applicant.identityDocument.series', code: 'WARN' },
    { path: 'applicant.identityDocument.issueDate.value', code: 'BIRTHPLACE_CERT_WARN' },
    { path: 'representative.representativeProof.series', code: 'WARN' },
    { path: 'representative.representativeProof.issueDate.value', code: 'BIRTHPLACE_CERT_WARN' },
    { path: 'independentChildDocument.series', code: 'WARN' },
    { path: 'applicant.personIdentity.fullName.firstName', code: 'WARN_FORMAT' },
    { path: 'applicant.personIdentity.fullName.lastName', code: 'WARN_FORMAT' },
    { path: 'applicant.personIdentity.fullName.middleName.value', code: 'WARN_FORMAT' },
    { path: 'applicant.nameChanges.lastName', code: 'WARN_FORMAT' },
    { path: 'applicant.nameChanges.firstName', code: 'WARN_FORMAT' },
    { path: 'applicant.nameChanges.middleName.value', code: 'WARN_FORMAT' },
    { path: 'representative.personIdentity.fullName.lastName', code: 'WARN_FORMAT' },
    { path: 'representative.personIdentity.fullName.firstName', code: 'WARN_FORMAT' },
    { path: 'representative.personIdentity.fullName.middleName.value', code: 'WARN_FORMAT' },
    { path: 'representative.nameChanges.lastName', code: 'WARN_FORMAT' },
    { path: 'representative.nameChanges.firstName', code: 'WARN_FORMAT' },
    { path: 'representative.nameChanges.middleName.value', code: 'WARN_FORMAT' },
    { path: 'representative.trustRecipient.personIdentity.fullName.lastName', code: 'WARN_FORMAT' },
    { path: 'representative.trustRecipient.personIdentity.fullName.firstName', code: 'WARN_FORMAT' },
    { path: 'representative.trustRecipient.personIdentity.fullName.middleName.value', code: 'WARN_FORMAT' },
    { path: 'applicant.jobRecords', code: 'WARN_FORMAT' },
    { path: 'applicant.activeFPassports.p1.series', code: 'FOREIGN_PASSPORT_UNKNOWN_SERIES' },
    { path: 'applicant.activeFPassports.p2.series', code: 'FOREIGN_PASSPORT_UNKNOWN_SERIES' },
    { path: 'applicant.birthPlaceLat', code: 'BIRTHPLACE_MISMATCH' },
    { path: 'applicant.birthPlaceRus', code: 'BIRTHPLACE_MISMATCH' },
    { path: 'applicant.birthPlaceFull', code: 'BIRTHPLACE_INSUFFICIENT' },
    { path: 'applicant.permAddress.address.country', code: 'INCORRECT_COUNTRY_NAME' },
    { path: 'representative.permAddress.address.country', code: 'INCORRECT_COUNTRY_NAME' },
    { path: 'applicant.tempAddress.address.country', code: 'INCORRECT_COUNTRY_NAME' },
    { path: 'representative.tempAddress.address.country', code: 'INCORRECT_COUNTRY_NAME' },
  ];

  static readonly notifications: Array<ValidationSeveritySetting> = [
    { path: 'applicant.fingerprints.hash', code: 'REQUIRED' },
    { path: 'applicant.fingerprints.cert', code: 'REQUIRED' },
    { path: 'applicant.fingerprints.ekey', code: 'REQUIRED' },
    { path: 'applicant.fingerprints.digestURN', code: 'REQUIRED' },
    { path: 'applicant.fingerprints.transportURN', code: 'REQUIRED' },
    // Добавил в эту строгость по задаче Bug #33041
    { path: 'applicant.permAddress.index', code: 'REQUIRED' },
    { path: 'applicant.permAddress.admDivision.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'applicant.permAddress.street.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'applicant.permAddress.building.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'applicant.permAddress.housing.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'applicant.permAddress.structure.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'applicant.permAddress.flat.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'applicant.tempAddress.index', code: 'REQUIRED' },
    { path: 'applicant.tempAddress.admDivision.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'applicant.tempAddress.street.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'applicant.tempAddress.building.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'applicant.tempAddress.housing.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'applicant.tempAddress.structure.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'applicant.tempAddress.flat.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'applicant.birthPlaceRus', code: 'BIRTHPLACE_INSUFFICIENT' },
    { path: 'applicant.personIdentity.fullName.middleName.value', code: 'MIDDLE_NAME_STUBS' },
    { path: 'representative.personIdentity.fullName.middleName.value', code: 'MIDDLE_NAME_STUBS' },
    { path: 'representative.trustRecipient.personIdentity.fullName.middleName.value', code: 'MIDDLE_NAME_STUBS' },
    { path: 'applicant.nameChanges.middleName.value', code: 'MIDDLE_NAME_STUBS' },
    { path: 'representative.nameChanges.middleName.value', code: 'MIDDLE_NAME_STUBS' },
  ];

  static readonly notificationsMobile: Array<ValidationSeveritySetting> = [
    { path: 'applicant.chipPhoto.scanned', code: 'NOT_ALLOWED_VALUE' },
    { path: 'applicant.printPhoto.scanned', code: 'NOT_ALLOWED_VALUE' },
    { path: 'applicant.signature.scanned', code: 'NOT_ALLOWED_VALUE' },
    { path: 'applicant.fingerprints.hash', code: 'REQUIRED' },
    { path: 'applicant.fingerprints.cert', code: 'REQUIRED' },
    { path: 'applicant.fingerprints.ekey', code: 'REQUIRED' },
    { path: 'applicant.fingerprints.digestURN', code: 'REQUIRED' },
    { path: 'applicant.fingerprints.transportURN', code: 'REQUIRED' },
    { path: 'applicant.secondNationality.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'dipServiceControlData.secretAdmitted.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'dipServiceControlData.secretAware.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'dipServiceControlData.trips.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'dipServiceControlData.contracts.value', code: 'CANNOT_BE_EMPTY' },
    { path: 'receptionUser', code: 'REQUIRED' },
  ];

  static readonly spec = {
    applicant: ValidationRules._object({
      gender: ValidationRules._enum('AlwaysVisible', '0', '1'),
      personIdentity: ValidationRules._object({
        fullName: ValidationRules._object({
          lastName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 34 },
            { like: Validation.reCyrillicFullNameWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
          ),
          firstName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 40 },
            { max_length_sum: { with: 'applicant.personIdentity.fullName.middleName.value', sum: 39 } },
            { like: Validation.reCyrillicFullNameWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
            { first_name_gender: { gender: 'applicant.gender.value' } },
          ),
          middleName: ValidationRules._nullable_string(
            'AlwaysVisible',
            { max_length: 38 },
            { max_length_sum: { with: 'applicant.personIdentity.fullName.firstName', sum: 39 } },
            { like: Validation.reCyrillicFullNameWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
            { middle_name_gender: { gender: 'applicant.gender.value' } },
          ),
        }),
        birthDate: ValidationRules._date('AlwaysVisible', {
          utc_date: { dmy: true, my: false, y: true, range: [{ min: '1900-01-01' }, { max: 'today' }] },
        }),
      }),
      nameChanges: ValidationRules._list(
        {
          lastName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reNameChangePartWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reNameChangePartStrict } },
          ),
          firstName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reNameChangePartWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reNameChangePartStrict } },
          ),
          middleName: ValidationRules._nullable_string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reNameChangePartWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reNameChangePartStrict } },
          ),
          year: ValidationRules._date('AlwaysVisible', {
            utc_date: {
              dmy: false,
              my: false,
              y: true,
              range: [{ min: 'applicant.personIdentity.birthDate.value', up_val: true, err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
            },
          }),
          location: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reNameChangeLocation }),
        },
        { old_fio: { fio: 'applicant.personIdentity.fullName' } },
      ),
      lastNameLat: ValidationRules._string('AlwaysVisible', { max_length: 36 }, { like: Validation.reLatinFullName }),
      firstNameLat: ValidationRules._string('AlwaysVisible', { max_length: 42 }, { like: Validation.reLatinFullName }),
      birthPlaceFull: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
      birthPlaceRus: ValidationRules._string(
        'AlwaysVisible',
        { max_length: 35 },
        { max_length_sum: { with: 'applicant.birthPlaceLat', sum: 38 } },
        { like: Validation.reCyrillicBirthPlace },
        { birthplace_mismatch: { birthDate: 'applicant.personIdentity.birthDate', birthPlace: 'applicant.birthPlaceRus' } },
      ),
      birthPlaceLat: ValidationRules._string(
        'AlwaysVisible',
        { max_length: 35 },
        { max_length_sum: { with: 'applicant.birthPlaceRus', sum: 38 } },
        { like: Validation.reLatinBirthPlace },
        { birthplace_mismatch: { birthDate: 'applicant.personIdentity.birthDate', birthPlace: 'applicant.birthPlaceLat' } },
      ),
      secondNationality: ValidationRules._nullable_string('AlwaysVisible', { max_length: 35 }, { like: Validation.reCountry }),
      permAddress: ValidationRules._object({
        index: ValidationRules._string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        country: ValidationRules._ostring('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
        admDivision: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
        settlement: ValidationRules._ostring(
          'AlwaysVisible',
          { max_length: 255 },
          { like: Validation.reAddressPartName },
          { restrictions_for_common_passsport_type: { passportType: 'desiredForeignPassportType' } },
        ),
        street: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
        building: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        housing: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        structure: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        flat: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        phone: ValidationRules._ostring('AlwaysVisible', { max_length: 50 }, { like: Validation.rePhone }),
      }),
      tempAddress: ValidationRules._object({
        index: ValidationRules._string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        country: ValidationRules._ostring('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
        admDivision: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
        settlement: ValidationRules._ostring(
          'AlwaysVisible',
          { max_length: 255 },
          { like: Validation.reAddressPartName },
          { restrictions_for_common_passsport_type: { passportType: 'desiredForeignPassportType' } },
        ),
        street: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
        building: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        housing: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        structure: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        flat: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        phone: ValidationRules._ostring('AlwaysVisible', { max_length: 50 }, { like: Validation.rePhone }),
      }),
      activeFPassport: ValidationRules._bool('CanHide', 'false', 'true'),
      activeFPassports: ValidationRules._object(
        {
          p1: ValidationRules._hideable_object({
            series: ValidationRules._string('AlwaysVisible', { length_equal: 2 }, { like: Validation.reDigit }, 'foreign_passport_series'),
            number: ValidationRules._string('AlwaysVisible', { length_equal: 7 }, { like: Validation.reDigit }),
            issuer: ValidationRules._string('AlwaysVisible', { length_between: [2, 255] }, { like: Validation.reSpecial1 }),
            issueDate: ValidationRules._date(
              'AlwaysVisible',
              {
                utc_date: {
                  dmy: true,
                  my: false,
                  y: false,
                  range: [
                    { min: 'applicant.personIdentity.birthDate.value', offset_val: { d: -1 }, err: 'LESS_THAN_BIRTHDATE' },
                    { max: 'today' },
                  ],
                },
              },
              { foreign_passport_issued: { series: 'applicant.activeFPassports.p1.series' } },
            ),
            mustBeCanceled: ValidationRules._bool('CanHide', 'false', 'true'),
            next: ValidationRules._bool('CanHide', 'false', 'true'),
          }),
          p2: ValidationRules._hideable_object({
            series: ValidationRules._string('AlwaysVisible', { length_equal: 2 }, { like: Validation.reDigit }, 'foreign_passport_series'),
            number: ValidationRules._string('AlwaysVisible', { length_equal: 7 }, { like: Validation.reDigit }),
            issuer: ValidationRules._string('AlwaysVisible', { length_between: [2, 255] }, { like: Validation.reSpecial1 }),
            issueDate: ValidationRules._date(
              'AlwaysVisible',
              {
                utc_date: {
                  dmy: true,
                  my: false,
                  y: false,
                  range: [
                    { min: 'applicant.personIdentity.birthDate.value', offset_val: { d: -1 }, err: 'LESS_THAN_BIRTHDATE' },
                    { max: 'today' },
                  ],
                },
              },
              { foreign_passport_issued: { series: 'applicant.activeFPassports.p2.series' } },
            ),
            mustBeCanceled: ValidationRules._bool('CanHide', 'false', 'true'),
            next: ValidationRules._bool('CanHide', 'false'),
          }),
        },
        'foreign_passport_canceled',
      ),
      jobRecords: ValidationRules._hideable_list(
        {
          position: ValidationRules._string('AlwaysVisible', { max_length: 5530 }, { like: Validation.reEmployerCyrillic }),
          address: ValidationRules._string('AlwaysVisible', { max_length: 5530 }, { like: Validation.reEmployerCyrillic }),
          from: ValidationRules._date('AlwaysVisible', {
            utc_date: {
              dmy: false,
              my: true,
              y: false,
              range: [{ min: 'applicant.personIdentity.birthDate.value', up_val: true, err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
            },
          }),
          to: ValidationRules._nullable_date('AlwaysVisible', {
            utc_date: {
              dmy: false,
              my: true,
              y: false,
              range: [
                { min: 'applicant.personIdentity.birthDate.value', up_val: true, err: 'LESS_THAN_BIRTHDATE' },
                { min: '^^.from.value', err: 'LESS_THAN_FROM' },
                { max: 'today' },
              ],
            },
          }),
        },
        'any_job',
        'job_break',
      ),
      jobManDate: ValidationRules._date('CanHide', {
        utc_date: {
          dmy: true,
          my: false,
          y: false,
          range: [{ min: 'today', offset_edge: { y: -2 }, err: 'DOCUMENT_AGE_GREATER_THAN_2' }, { max: 'today' }],
        },
      }),
      jobManName: ValidationRules._string('CanHide', { max_length: 255 }, { like: Validation.reSpecial1 }),
      identityDocument: ValidationRules._variable_object(
        'type',
        {
          '': {
            type: ValidationRules._enum('AlwaysVisible'),
          },
          '1': {
            type: ValidationRules._enum('AlwaysVisible', '1'),
            series: ValidationRules._ostring(
              'AlwaysVisible',
              { for_warning: { tag: 'WARN' } },
              { max_length: 36 },
              { like: Validation.reBirthCertificateSeries },
            ),
            number: ValidationRules._string('AlwaysVisible', { max_length: 36 }, { like: Validation.reAnyDocumentNumber }),
            issuer: ValidationRules._string('AlwaysVisible', { length_between: [2, 255] }, { like: Validation.reAnyDocumentIssuer }),
            issueDate: ValidationRules._date(
              'AlwaysVisible',
              {
                utc_date: {
                  dmy: true,
                  my: false,
                  y: false,
                  range: [{ min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
                },
              },
              { birthdate_certificate_mismatched: { nameChanges: 'applicant.nameChanges' } },
            ),
          },
          '3': {
            type: ValidationRules._enum('AlwaysVisible', '3'),
            series: ValidationRules._string('AlwaysVisible', { length_equal: 4 }, { like: Validation.reDigit }),
            number: ValidationRules._string('AlwaysVisible', { length_equal: 6 }, { like: Validation.reDigit }),
            issuer: ValidationRules._string('AlwaysVisible', { length_between: [2, 255] }, { like: Validation.reAnyDocumentIssuer }),
            issueDate: ValidationRules._date(
              'AlwaysVisible',
              {
                utc_date: {
                  dmy: true,
                  my: false,
                  y: false,
                  range: [
                    { min: '1997-01-01', err: 'LESS_THAN_YEAR_OF_1997' },
                    { min: 'applicant.personIdentity.birthDate.value', offset_edge: { y: 14 }, err: 'LESS_THAN_AGE_OF_14' },
                    { max: 'today' },
                  ],
                },
              },
              { russian_passport_issued: { birthDate: 'applicant.personIdentity.birthDate.value' } },
              { identity_document_mismatched: { nameChanges: 'applicant.nameChanges' } },
            ),
          },
          '5': {
            type: ValidationRules._enum('AlwaysVisible', '5'),
          },
        },
        'applicant_identity_docs',
      ),
      chipPhoto: ValidationRules._image(),
      printPhoto: ValidationRules._image(),
      signature: ValidationRules._image(),
      fingerprints: ValidationRules._fingerprints(),
    }),
    representative: ValidationRules._hideable_object({
      gender: ValidationRules._enum('AlwaysVisible', '0', '1'),
      personIdentity: ValidationRules._object({
        fullName: ValidationRules._object({
          lastName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reCyrillicFullNameWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
          ),
          firstName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reCyrillicFullNameWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
            { first_name_gender: { gender: 'representative.gender.value' } },
          ),
          middleName: ValidationRules._nullable_string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reCyrillicFullNameWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
            { middle_name_gender: { gender: 'representative.gender.value' } },
          ),
        }),
        birthDate: ValidationRules._date('AlwaysVisible', {
          utc_date: {
            dmy: true,
            my: false,
            y: true,
            range: [{ min: '1900-01-01' }, { max: 'today' }, { max: 'today', offset_val: { y: 16 }, err: 'PERSON_AGE_LESS_THAN_16' }],
          },
        }),
      }),
      birthPlace: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
      address: ValidationRules._object({
        text: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
      }),
      nameChanges: ValidationRules._list(
        {
          lastName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reNameChangePartWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reNameChangePartStrict } },
          ),
          firstName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reNameChangePartWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reNameChangePartStrict } },
          ),
          middleName: ValidationRules._nullable_string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reNameChangePartWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reNameChangePartStrict } },
          ),
          year: ValidationRules._date('AlwaysVisible', {
            utc_date: {
              dmy: false,
              my: false,
              y: true,
              range: [{ min: 'representative.personIdentity.birthDate.value', up_val: true, err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
            },
          }),
          location: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reNameChangeLocation }),
        },
        { old_fio: { fio: 'representative.personIdentity.fullName' } },
      ),
      identityDocument: ValidationRules._variable_object('type', {
        '': {
          type: ValidationRules._enum('AlwaysVisible'),
        },
        '0': {
          type: ValidationRules._enum('AlwaysVisible', '0'),
          series: ValidationRules._string('AlwaysVisible', { max_length: 36 }, { like: Validation.reAnyDocumentSeries }),
          number: ValidationRules._string('AlwaysVisible', { max_length: 36 }, { like: Validation.reAnyDocumentNumber }),
          issuer: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
          issueDate: ValidationRules._date('AlwaysVisible', {
            utc_date: {
              dmy: true,
              my: false,
              y: false,
              range: [{ min: 'representative.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
            },
          }),
        },
        '3': {
          type: ValidationRules._enum('AlwaysVisible', '3'),
          series: ValidationRules._string('AlwaysVisible', { length_equal: 4 }, { like: Validation.reDigit }),
          number: ValidationRules._string('AlwaysVisible', { length_equal: 6 }, { like: Validation.reDigit }),
          issuer: ValidationRules._string('AlwaysVisible', { length_between: [2, 255] }, { like: Validation.reCommon }),
          issueDate: ValidationRules._date(
            'AlwaysVisible',
            {
              utc_date: {
                dmy: true,
                my: false,
                y: false,
                range: [
                  { min: '1997-01-01', err: 'LESS_THAN_YEAR_OF_1997' },
                  { min: 'representative.personIdentity.birthDate.value', offset_edge: { y: 14 }, err: 'LESS_THAN_AGE_OF_14' },
                  { max: 'today' },
                ],
              },
            },
            { russian_passport_issued: { birthDate: 'representative.personIdentity.birthDate.value' } },
            { identity_document_mismatched: { nameChanges: 'representative.nameChanges' } },
          ),
        },
        '4': {
          type: ValidationRules._enum('AlwaysVisible', '4'),
          series: ValidationRules._string('AlwaysVisible', { length_equal: 2 }, { like: Validation.reDigit }),
          number: ValidationRules._string('AlwaysVisible', { length_equal: 7 }, { like: Validation.reDigit }),
          issuer: ValidationRules._string('AlwaysVisible', { length_between: [2, 255] }, { like: Validation.reSpecial1 }),
          issueDate: ValidationRules._date(
            'AlwaysVisible',
            {
              utc_date: {
                dmy: true,
                my: false,
                y: false,
                range: [
                  { min: 'representative.personIdentity.birthDate.value', offset_val: { d: -1 }, err: 'LESS_THAN_BIRTHDATE' },
                  { max: 'today' },
                ],
              },
            },
            { foreign_passport_issued: { series: 'representative.identityDocument.series' } },
          ),
        },
      }),
    }),
    goal: ValidationRules._enum('AlwaysVisible', '0', '1'),
    residenceCountry: ValidationRules._string('CanHide', { max_length: 35 }, { like: Validation.reCountry }),
    cause: ValidationRules._enum('AlwaysVisible', '0', '1', '2', '3', '5'),
    secretAccess: ValidationRules._nullable_string('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
    departureRestrictions: ValidationRules._nullable_string('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
    militaryService: ValidationRules._nullable_string('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
    conviction: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
    legalObligations: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
    mailing: ValidationRules._hideable_object({
      active: ValidationRules._bool('AlwaysVisible', 'false', 'true'),
      addressRus: ValidationRules._string('CanHide', { max_length: 1024 }, { like: Validation.reExtended }),
      addressLat: ValidationRules._string('CanHide', { max_length: 1024 }, { like: Validation.reExtended }),
    }),
    citizenshipCertificate: ValidationRules._hideable_object({
      active: ValidationRules._bool('AlwaysVisible', 'false', 'true'),
      number: ValidationRules._string('CanHide', { max_length: 255 }, { like: Validation.reSpecial2 }),
      date: ValidationRules._date('CanHide', {
        utc_date: {
          dmy: true,
          my: false,
          y: false,
          range: [
            { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
            { min: 'today', offset_edge: { y: -1 }, err: 'DOCUMENT_AGE_GREATER_THAN_1' },
            { max: 'today' },
          ],
        },
      }),
      officerPost: ValidationRules._string('CanHide', { max_length: 255 }, { like: Validation.reSpecial1 }),
      officerName: ValidationRules._string('CanHide', { max_length: 255 }, { like: Validation.reSpecial1 }),
      testimony: ValidationRules._string('CanHide', { max_length: 4096 }, { like: Validation.reCommon }),
    }),
    adultSpecialConditions: ValidationRules._enum('CanHide', '0', '1', '2', '3'),
    teenagerSpecialConditions: ValidationRules._enum('CanHide', '0', '1', '2'),
    presidentEdict: ValidationRules._hideable_object({
      number: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
      date: ValidationRules._date('AlwaysVisible', {
        utc_date: {
          dmy: true,
          my: false,
          y: false,
          range: [
            { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
            { min: 'today', offset_edge: { m: -3 }, err: 'DOCUMENT_AGE_GREATER_THAN_3' },
            { max: 'today' },
          ],
        },
      }),
    }),
    citizenshipDecision: ValidationRules._hideable_object({
      number: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
      date: ValidationRules._date('AlwaysVisible', {
        utc_date: {
          dmy: true,
          my: false,
          y: false,
          range: [
            { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
            { min: 'today', offset_edge: { m: -3 }, err: 'DOCUMENT_AGE_GREATER_THAN_3' },
            { max: 'today' },
          ],
        },
      }),
      officerPost: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reOfficerPost }),
      officerName: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reSpecial1 }),
      article: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
    }),
    soldierPermission: ValidationRules._hideable_object({
      number: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAnyDocumentNumber }),
      date: ValidationRules._date('AlwaysVisible', {
        utc_date: {
          dmy: true,
          my: false,
          y: false,
          range: [{ min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
        },
      }),
      signerPost: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
      signerName: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCyrillicFullNameWeak }),
      agreementNumber: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
      agreementDate: ValidationRules._date('AlwaysVisible', {
        utc_date: {
          dmy: true,
          my: false,
          y: false,
          range: [{ min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
        },
      }),
      deciderName: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCyrillicFullNameWeak }),
      militaryUnit: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
    }),
    fingerprintsRequired: ValidationRules._enum('CanHide', '0', '1'),
    fingerprintsPresent: ValidationRules._bool('CanHide', 'false', 'true'),
    receptionUser: ValidationRules._string('CanHide', { max_length: 255 }, { like: Validation.reReceptionUser }),
    receptionDate: ValidationRules._date('AlwaysVisible', { utc_date: { dmy: true, my: false, y: false, range: [] } }),
    inputMedia: ValidationRules._enum('AlwaysVisible', '0', '1', '2', '3', '4', '5'),
    mobileOptions: ValidationRules._hideable_object({
      department: ValidationRules._string('AlwaysVisible'),
      departmentUsesPassportMail: ValidationRules._bool('AlwaysVisible', 'false', 'true'),
      departmentUsesInternetApps: ValidationRules._bool('AlwaysVisible', 'false', 'true'),
      sendAppViaInternet: ValidationRules._bool('CanHide', 'false', 'true'),
      sendAppViaInternetWithFIO: ValidationRules._bool('CanHide', 'false', 'true'),
    }),
  };

  static readonly spec2 = {
    applicant: ValidationRules._object({
      gender: ValidationRules._enum('AlwaysVisible', '0', '1'),
      personIdentity: ValidationRules._object({
        fullName: ValidationRules._object({
          lastName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 34 },
            { like: Validation.reCyrillicFullNameWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
          ),
          firstName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 40 },
            { max_length_sum: { with: 'applicant.personIdentity.fullName.middleName.value', sum: 39 } },
            { like: Validation.reCyrillicFullNameWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
            { first_name_gender: { gender: 'applicant.gender.value' } },
          ),
          middleName: ValidationRules._nullable_string(
            'AlwaysVisible',
            { max_length: 38 },
            { max_length_sum: { with: 'applicant.personIdentity.fullName.firstName', sum: 39 } },
            { like: Validation.reCyrillicFullNameWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
            { middle_name_gender: { gender: 'applicant.gender.value' } },
            { middle_name_stubs: { middleName: 'applicant.personIdentity.fullName.middleName' } },
          ),
        }),
        birthDate: ValidationRules._date('AlwaysVisible', {
          utc_date: { dmy: true, my: false, y: true, range: [{ min: '1900-01-01' }, { max: 'today' }] },
        }),
      }),
      nameChanges: ValidationRules._list(
        {
          lastName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reNameChangePartWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reNameChangePartStrict } },
          ),
          firstName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reNameChangePartWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reNameChangePartStrict } },
          ),
          middleName: ValidationRules._nullable_string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reNameChangePartWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reNameChangePartStrict } },
            { like_ex: { tag: 'MIDDLE_NAME_STUBS', regexp: Validation.reMiddleNameStubs } },
          ),
          year: ValidationRules._date('AlwaysVisible', {
            utc_date: {
              dmy: true,
              my: false,
              y: false,
              range: [{ min: 'applicant.personIdentity.birthDate.value', up_val: true, err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
            },
          }),
          location: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reNameChangeLocation }),
        },
        { old_fio: { fio: 'applicant.personIdentity.fullName' } },
      ),
      lastNameLat: ValidationRules._string('AlwaysVisible', { max_length: 36 }, { like: Validation.reLatinFullName }),
      firstNameLat: ValidationRules._string('AlwaysVisible', { max_length: 42 }, { like: Validation.reLatinFullName }),
      birthPlaceFull: ValidationRules._ostring(
        'AlwaysVisible',
        { max_length: 255 },
        { like: Validation.reCommon },
        { birthplace_insufficient: { birthplace: 'applicant.birthPlaceFull' } },
        { restrictions_for_common_passsport_type: { passportType: 'desiredForeignPassportType' } },
      ),
      birthPlaceRus: ValidationRules._string(
        'AlwaysVisible',
        { max_length: 35 },
        { max_length_sum: { with: 'applicant.birthPlaceLat', sum: 38 } },
        { like: Validation.reCyrillicBirthPlace },
        { birthplace_mismatch: { birthDate: 'applicant.personIdentity.birthDate', birthPlace: 'applicant.birthPlaceRus' } },
      ),
      birthPlaceLat: ValidationRules._string(
        'AlwaysVisible',
        { max_length: 35 },
        { max_length_sum: { with: 'applicant.birthPlaceRus', sum: 38 } },
        { like: Validation.reLatinBirthPlace },
        { birthplace_mismatch: { birthDate: 'applicant.personIdentity.birthDate', birthPlace: 'applicant.birthPlaceLat' } },
      ),
      permAddress: ValidationRules._object({
        address: ValidationRules._object({
          country: ValidationRules._ostring(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reAddressPartName },
            { incorrect_country_name: { countryName: 'applicant.permAddress.address.country' } },
            { restrictions_for_common_passsport_type: { passportType: 'desiredForeignPassportType' } },
          ),
          admDivision: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          region: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          settlement: ValidationRules._ostring(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reAddressPartName },
            { restrictions_for_common_passsport_type: { passportType: 'desiredForeignPassportType' } },
          ),
          street: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          building: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
          housing: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
          structure: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
          flat: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        }),
        from: ValidationRules._odate('AlwaysVisible', {
          utc_date: {
            dmy: true,
            my: false,
            y: false,
            range: [
              { min: '1900-01-01' },
              { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
              { max: 'today' },
            ],
          },
        }),
      }),
      tempAddress: ValidationRules._object({
        type: ValidationRules._enum('AlwaysVisible', '0', '1'),
        address: ValidationRules._object({
          country: ValidationRules._ostring(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reAddressPartName },
            { incorrect_country_name: { countryName: 'applicant.tempAddress.address.country' } },
            { restrictions_for_common_passsport_type: { passportType: 'desiredForeignPassportType' } },
          ),
          admDivision: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          region: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          settlement: ValidationRules._ostring(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reAddressPartName },
            { restrictions_for_common_passsport_type: { passportType: 'desiredForeignPassportType' } },
          ),
          street: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          building: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
          housing: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
          structure: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
          flat: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        }),
        from: ValidationRules._odate('AlwaysVisible', {
          utc_date: {
            dmy: true,
            my: false,
            y: false,
            range: [
              { min: '1900-01-01' },
              { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
              { max: 'today' },
            ],
          },
        }),
        to: ValidationRules._odate('AlwaysVisible', {
          utc_date: {
            dmy: true,
            my: false,
            y: false,
            range: [
              { min: '1900-01-01' },
              { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
              { min: '^^.from.value', err: 'LESS_THAN_FROM' },
            ],
          },
        }),
      }),
      email: ValidationRules._ostring('AlwaysVisible', { max_length: 100 }, { like: Validation.reEmail }, { email: {} }),
      phone: ValidationRules._ostring('AlwaysVisible', { max_length: 50 }, { like: Validation.rePhone }),
      activeFPassport: ValidationRules._bool('CanHide', 'false', 'true'),
      activeFPassports: ValidationRules._object(
        {
          p1: ValidationRules._hideable_object({
            series: ValidationRules._string('AlwaysVisible', { length_equal: 2 }, { like: Validation.reDigit }, 'foreign_passport_series'),
            number: ValidationRules._string('AlwaysVisible', { length_equal: 7 }, { like: Validation.reDigit }),
            issuer: ValidationRules._string('AlwaysVisible', { length_between: [2, 255] }, { like: Validation.reSpecial1 }),
            issueDate: ValidationRules._date(
              'AlwaysVisible',
              {
                utc_date: {
                  dmy: true,
                  my: false,
                  y: false,
                  range: [
                    { min: 'applicant.personIdentity.birthDate.value', offset_val: { d: -1 }, err: 'LESS_THAN_BIRTHDATE' },
                    { max: 'today' },
                  ],
                },
              },
              { foreign_passport_issued: { series: 'applicant.activeFPassports.p1.series' } },
            ),
            mustBeCanceled: ValidationRules._bool('CanHide', 'false', 'true'),
            lost: ValidationRules._bool('CanHide', 'false', 'true'),
            next: ValidationRules._bool('CanHide', 'false', 'true'),
          }),
          p2: ValidationRules._hideable_object({
            series: ValidationRules._string('AlwaysVisible', { length_equal: 2 }, { like: Validation.reDigit }, 'foreign_passport_series'),
            number: ValidationRules._string('AlwaysVisible', { length_equal: 7 }, { like: Validation.reDigit }),
            issuer: ValidationRules._string('AlwaysVisible', { length_between: [2, 255] }, { like: Validation.reSpecial1 }),
            issueDate: ValidationRules._date(
              'AlwaysVisible',
              {
                utc_date: {
                  dmy: true,
                  my: false,
                  y: false,
                  range: [
                    { min: 'applicant.personIdentity.birthDate.value', offset_val: { d: -1 }, err: 'LESS_THAN_BIRTHDATE' },
                    { max: 'today' },
                  ],
                },
              },
              { foreign_passport_issued: { series: 'applicant.activeFPassports.p2.series' } },
            ),
            mustBeCanceled: ValidationRules._bool('CanHide', 'false', 'true'),
            lost: ValidationRules._bool('CanHide', 'false', 'true'),
            next: ValidationRules._bool('CanHide', 'false'),
          }),
        },
        'foreign_passport_canceled',
        'foreign_passport_lost',
      ),
      jobRecords: ValidationRules._hideable_list(
        {
          position: ValidationRules._string('AlwaysVisible', { max_length: 5530 }, { like: Validation.reEmployerCyrillic }),
          address: ValidationRules._string('AlwaysVisible', { max_length: 5530 }, { like: Validation.reEmployerCyrillic }),
          from: ValidationRules._date('AlwaysVisible', {
            utc_date: {
              dmy: false,
              my: true,
              y: false,
              range: [{ min: 'applicant.personIdentity.birthDate.value', up_val: true, err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
            },
          }),
          to: ValidationRules._nullable_date('AlwaysVisible', {
            utc_date: {
              dmy: false,
              my: true,
              y: false,
              range: [
                { min: 'applicant.personIdentity.birthDate.value', up_val: true, err: 'LESS_THAN_BIRTHDATE' },
                { min: '^^.from.value', err: 'LESS_THAN_FROM' },
                { max: 'today' },
              ],
            },
          }),
        },
        'any_job',
        'job_break',
      ),
      jobManDate: ValidationRules._odate('CanHide', {
        utc_date: {
          dmy: true,
          my: false,
          y: false,
          range: [{ min: 'today', offset_edge: { y: -2 }, err: 'DOCUMENT_AGE_GREATER_THAN_2' }, { max: 'today' }],
        },
      }),
      jobManName: ValidationRules._string('CanHide', { max_length: 255 }, { like: Validation.reSpecial1 }),
      hrManager: ValidationRules._hideable_object({
        position: ValidationRules._ostring('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
        fullName: ValidationRules._object({
          lastName: ValidationRules._ostring('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
          firstName: ValidationRules._ostring('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
          middleName: ValidationRules._onullable_string('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
        }),
        phone: ValidationRules._ostring('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
      }),
      identityDocument: ValidationRules._variable_object(
        'type',
        {
          '': {
            type: ValidationRules._enum('AlwaysVisible'),
          },
          '1': {
            type: ValidationRules._enum('AlwaysVisible', '1'),
            series: ValidationRules._ostring(
              'AlwaysVisible',
              { for_warning: { tag: 'WARN' } },
              { max_length: 36 },
              { like: Validation.reBirthCertificateSeries },
            ),
            number: ValidationRules._string('AlwaysVisible', { max_length: 36 }, { like: Validation.reAnyDocumentNumber }),
            issuer: ValidationRules._string('AlwaysVisible', { length_between: [2, 255] }, { like: Validation.reAnyDocumentIssuer }),
            issueDate: ValidationRules._date(
              'AlwaysVisible',
              {
                utc_date: {
                  dmy: true,
                  my: false,
                  y: false,
                  range: [{ min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
                },
              },
              { birthdate_certificate_mismatched: { nameChanges: 'applicant.nameChanges' } },
            ),
          },
          '3': {
            type: ValidationRules._enum('AlwaysVisible', '3'),
            series: ValidationRules._string('AlwaysVisible', { length_equal: 4 }, { like: Validation.reDigit }),
            number: ValidationRules._string('AlwaysVisible', { length_equal: 6 }, { like: Validation.reDigit }),
            issuer: ValidationRules._string('AlwaysVisible', { length_between: [2, 255] }, { like: Validation.reAnyDocumentIssuer }),
            issueDate: ValidationRules._date(
              'AlwaysVisible',
              {
                utc_date: {
                  dmy: true,
                  my: false,
                  y: false,
                  range: [
                    { min: '1997-01-01', err: 'LESS_THAN_YEAR_OF_1997' },
                    { min: 'applicant.personIdentity.birthDate.value', offset_edge: { y: 14 }, err: 'LESS_THAN_AGE_OF_14' },
                    { max: 'today' },
                  ],
                },
              },
              { russian_passport_issued: { birthDate: 'applicant.personIdentity.birthDate.value' } },
              { identity_document_mismatched: { nameChanges: 'applicant.nameChanges' } },
            ),
          },
          '5': {
            type: ValidationRules._enum('AlwaysVisible', '5'),
          },
        },
        'applicant_identity_docs',
      ),
      chipPhoto: ValidationRules._image(),
      printPhoto: ValidationRules._image(),
      signature: ValidationRules._image(),
      fingerprints: ValidationRules._fingerprints(),
      secondNationality: ValidationRules._nullable_string('CanHide', { max_length: 35 }, { like: Validation.reCountry }),
    }),
    dipServiceControlData: ValidationRules._hideable_object({
      dutyOrganization: ValidationRules._ostring('CanHide', { max_length: 255 }, { like: Validation.reDutyOrganization }),
      dutyPosition: ValidationRules._ostring('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
      dutyDuration: ValidationRules._ostring('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
      dutyCountry: ValidationRules._ostring('CanHide', { max_length: 255 }),
      secretAdmitted: ValidationRules._nullable_string('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
      secretAware: ValidationRules._nullable_string('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
      trips: ValidationRules._nullable_string('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
      contracts: ValidationRules._nullable_string('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
    }),
    representative: ValidationRules._hideable_object({
      gender: ValidationRules._enum('AlwaysVisible', '0', '1'),
      personIdentity: ValidationRules._object({
        fullName: ValidationRules._object({
          lastName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reCyrillicFullNameWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
          ),
          firstName: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reCyrillicFullNameWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
            { first_name_gender: { gender: 'representative.gender.value' } },
          ),
          middleName: ValidationRules._nullable_string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reCyrillicFullNameWeak },
            { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
            { middle_name_gender: { gender: 'representative.gender.value' } },
            { middle_name_stubs: { middleName: 'representative.personIdentity.fullName.middleName' } },
          ),
        }),
        birthDate: ValidationRules._date('AlwaysVisible', {
          utc_date: {
            dmy: true,
            my: false,
            y: true,
            range: [{ min: '1900-01-01' }, { max: 'today' }, { max: 'today', offset_val: { y: 16 }, err: 'PERSON_AGE_LESS_THAN_16' }],
          },
        }),
      }),
      birthPlace: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
      permAddress: ValidationRules._object({
        address: ValidationRules._object({
          country: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reAddressPartName },
            { incorrect_country_name: { countryName: 'representative.permAddress.address.country' } },
          ),
          admDivision: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          region: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          settlement: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          street: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          building: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
          housing: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
          structure: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
          flat: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        }),
        from: ValidationRules._odate('AlwaysVisible', {
          utc_date: {
            dmy: true,
            my: false,
            y: false,
            range: [
              { min: '1900-01-01' },
              { min: 'representative.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
              { max: 'today' },
            ],
          },
        }),
      }),
      tempAddress: ValidationRules._object({
        type: ValidationRules._enum('AlwaysVisible', '0', '1'),
        address: ValidationRules._object({
          country: ValidationRules._string(
            'AlwaysVisible',
            { max_length: 255 },
            { like: Validation.reAddressPartName },
            { incorrect_country_name: { countryName: 'representative.tempAddress.address.country' } },
          ),
          admDivision: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          region: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          settlement: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          street: ValidationRules._nullable_string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAddressPartName }),
          building: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
          housing: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
          structure: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
          flat: ValidationRules._nullable_string('AlwaysVisible', { max_length: 20 }, { like: Validation.reCommon }),
        }),
        from: ValidationRules._odate('AlwaysVisible', {
          utc_date: {
            dmy: true,
            my: false,
            y: false,
            range: [
              { min: '1900-01-01' },
              { min: 'representative.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
              { max: 'today' },
            ],
          },
        }),
        to: ValidationRules._odate('AlwaysVisible', {
          utc_date: {
            dmy: true,
            my: false,
            y: false,
            range: [
              { min: '1900-01-01' },
              { min: 'representative.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
              { min: '^^.from.value', err: 'LESS_THAN_FROM' },
            ],
          },
        }),
      }),
      email: ValidationRules._ostring('AlwaysVisible', { max_length: 100 }, { like: Validation.reEmail }, { email: {} }),
      phone: ValidationRules._ostring('AlwaysVisible', { max_length: 50 }, { like: Validation.rePhone }),
      identityDocument: ValidationRules._variable_object('type', {
        '': {
          type: ValidationRules._enum('AlwaysVisible'),
        },
        '0': {
          type: ValidationRules._enum('AlwaysVisible', '0'),
          series: ValidationRules._string('AlwaysVisible', { max_length: 36 }, { like: Validation.reAnyDocumentSeries }),
          number: ValidationRules._string('AlwaysVisible', { max_length: 36 }, { like: Validation.reAnyDocumentNumber }),
          issuer: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAnyDocumentIssuer }),
          issueDate: ValidationRules._date('AlwaysVisible', {
            utc_date: {
              dmy: true,
              my: false,
              y: false,
              range: [{ min: 'representative.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
            },
          }),
        },
        '3': {
          type: ValidationRules._enum('AlwaysVisible', '3'),
          series: ValidationRules._string('AlwaysVisible', { length_equal: 4 }, { like: Validation.reDigit }),
          number: ValidationRules._string('AlwaysVisible', { length_equal: 6 }, { like: Validation.reDigit }),
          issuer: ValidationRules._string('AlwaysVisible', { length_between: [2, 255] }, { like: Validation.reAnyDocumentIssuer }),
          issueDate: ValidationRules._date(
            'AlwaysVisible',
            {
              utc_date: {
                dmy: true,
                my: false,
                y: false,
                range: [
                  { min: '1997-01-01', err: 'LESS_THAN_YEAR_OF_1997' },
                  { min: 'representative.personIdentity.birthDate.value', offset_edge: { y: 14 }, err: 'LESS_THAN_AGE_OF_14' },
                  { max: 'today' },
                ],
              },
            },
            { russian_passport_issued: { birthDate: 'representative.personIdentity.birthDate.value' } },
          ),
        },
        '4': {
          type: ValidationRules._enum('AlwaysVisible', '4'),
          series: ValidationRules._string('AlwaysVisible', { length_equal: 2 }, { like: Validation.reDigit }),
          number: ValidationRules._string('AlwaysVisible', { length_equal: 7 }, { like: Validation.reDigit }),
          issuer: ValidationRules._string('AlwaysVisible', { length_between: [2, 255] }, { like: Validation.reAnyDocumentIssuer }),
          issueDate: ValidationRules._date(
            'AlwaysVisible',
            {
              utc_date: {
                dmy: true,
                my: false,
                y: false,
                range: [
                  { min: 'representative.personIdentity.birthDate.value', offset_val: { d: -1 }, err: 'LESS_THAN_BIRTHDATE' },
                  { max: 'today' },
                ],
              },
            },
            { foreign_passport_issued: { series: 'representative.identityDocument.series' } },
          ),
        },
      }),
      representativeProof: ValidationRules._variable_object('type', {
        '': {
          type: ValidationRules._enum('AlwaysVisible'),
        },
        '1': {
          type: ValidationRules._enum('AlwaysVisible', '1'),
          series: ValidationRules._ostring(
            'AlwaysVisible',
            { for_warning: { tag: 'WARN' } },
            { max_length: 36 },
            { like: Validation.reBirthCertificateSeries },
          ),
          number: ValidationRules._string('AlwaysVisible', { max_length: 36 }, { like: Validation.reAnyDocumentNumber }),
          issuer: ValidationRules._string('AlwaysVisible', { length_between: [2, 255] }, { like: Validation.reAnyDocumentIssuer }),
          issueDate: ValidationRules._date(
            'AlwaysVisible',
            {
              utc_date: {
                dmy: true,
                my: false,
                y: false,
                range: [
                  { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_APP_BIRTHDATE' },
                  { min: 'representative.personIdentity.birthDate.value', err: 'LESS_THAN_REP_BIRTHDATE' },
                  { max: 'today' },
                ],
              },
            },
            { birthdate_certificate_mismatched: { nameChanges: 'applicant.nameChanges' } }
          ),
        },
        '6': {
          type: ValidationRules._enum('AlwaysVisible', '6'),
          series: ValidationRules._ostring(
            'AlwaysVisible',
            { for_warning: { tag: 'WARN' } },
            { max_length: 36 },
            { like: Validation.reAnyDocumentSeries },
          ),
          number: ValidationRules._string('AlwaysVisible', { max_length: 36 }, { like: Validation.reAnyDocumentNumber }),
          issuer: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAnyDocumentIssuer }),
          issueDate: ValidationRules._date('AlwaysVisible', {
            utc_date: {
              dmy: true,
              my: false,
              y: false,
              range: [
                { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_APP_BIRTHDATE' },
                { min: 'representative.personIdentity.birthDate.value', err: 'LESS_THAN_REP_BIRTHDATE' },
                { max: 'today' },
              ],
            },
          }),
        },
      }),
      trustRecipient: ValidationRules._object({
        active: ValidationRules._bool('CanHide', 'false', 'true'),
        gender: ValidationRules._enum('CanHide', '0', '1'),
        personIdentity: ValidationRules._hideable_object({
          fullName: ValidationRules._object({
            lastName: ValidationRules._string(
              'AlwaysVisible',
              { max_length: 255 },
              { like: Validation.reCyrillicFullNameWeak },
              { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
            ),
            firstName: ValidationRules._string(
              'AlwaysVisible',
              { max_length: 255 },
              { like: Validation.reCyrillicFullNameWeak },
              { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
              { first_name_gender: { gender: 'representative.trustRecipient.gender.value' } },
            ),
            middleName: ValidationRules._nullable_string(
              'AlwaysVisible',
              { max_length: 255 },
              { like: Validation.reCyrillicFullNameWeak },
              { like_ex: { tag: 'WARN_FORMAT', regexp: Validation.reCyrillicFullNameStrict } },
              { middle_name_gender: { gender: 'representative.trustRecipient.gender.value' } },
              { middle_name_stubs: { middleName: 'trustRecipient.personIdentity.fullName.middleName' } },
            ),
          }),
          birthDate: ValidationRules._date('AlwaysVisible', {
            utc_date: {
              dmy: true,
              my: false,
              y: true,
              range: [{ min: '1900-01-01' }, { max: 'today' }, { max: 'today', offset_val: { y: 16 }, err: 'PERSON_AGE_LESS_THAN_16' }],
            },
          }),
        }),
        birthPlace: ValidationRules._string('CanHide', { max_length: 255 }, { like: Validation.reCommon }),
      }),
    }),
    incapable: ValidationRules._bool('CanHide', 'false', 'true'),
    independentChild: ValidationRules._bool('CanHide', 'false', 'true'),
    independentChildDocument: ValidationRules._hideable_variable_object('type', {
      '': {
        type: ValidationRules._enum('AlwaysVisible'),
      },
      '7': {
        type: ValidationRules._enum('AlwaysVisible', '7'),
        series: ValidationRules._ostring(
          'AlwaysVisible',
          { for_warning: { tag: 'WARN' } },
          { max_length: 36 },
          { like: Validation.reAnyDocumentSeries },
        ),
        number: ValidationRules._string('AlwaysVisible', { max_length: 36 }, { like: Validation.reAnyDocumentNumber }),
        issuer: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAnyDocumentIssuer }),
        issueDate: ValidationRules._date('AlwaysVisible', {
          utc_date: {
            dmy: true,
            my: false,
            y: false,
            range: [
              { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
              { min: 'applicant.personIdentity.birthDate.value', offset_edge: { y: 14 }, err: 'LESS_THAN_AGE_OF_14' },
              { max: 'today' },
            ],
          },
        }),
      },
      '8': {
        type: ValidationRules._enum('AlwaysVisible', '8'),
        series: ValidationRules._ostring(
          'AlwaysVisible',
          { for_warning: { tag: 'WARN' } },
          { max_length: 36 },
          { like: Validation.reAnyDocumentSeries },
        ),
        number: ValidationRules._string('AlwaysVisible', { max_length: 36 }, { like: Validation.reAnyDocumentNumber }),
        issuer: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAnyDocumentIssuer }),
        issueDate: ValidationRules._date('AlwaysVisible', {
          utc_date: {
            dmy: true,
            my: false,
            y: false,
            range: [
              { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
              { min: 'applicant.personIdentity.birthDate.value', offset_edge: { y: 14 }, err: 'LESS_THAN_AGE_OF_14' },
              { max: 'today' },
            ],
          },
        }),
      },
      '9': {
        type: ValidationRules._enum('AlwaysVisible', '9'),
        series: ValidationRules._ostring(
          'AlwaysVisible',
          { for_warning: { tag: 'WARN' } },
          { max_length: 36 },
          { like: Validation.reAnyDocumentSeries },
        ),
        number: ValidationRules._string('AlwaysVisible', { max_length: 36 }, { like: Validation.reAnyDocumentNumber }),
        issuer: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAnyDocumentIssuer }),
        issueDate: ValidationRules._date('AlwaysVisible', {
          utc_date: {
            dmy: true,
            my: false,
            y: false,
            range: [
              { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
              { min: 'applicant.personIdentity.birthDate.value', offset_edge: { y: 14 }, err: 'LESS_THAN_AGE_OF_14' },
              { max: 'today' },
            ],
          },
        }),
      },
    }),
    cause: ValidationRules._enum('AlwaysVisible', '0', '3', '6', '7', '8'),
    secretAccess: ValidationRules._hideable_object({
      value: ValidationRules._enum('AlwaysVisible', '0', '1'),
      organization: ValidationRules._string('CanHide', { max_length: 4096 }, { like: Validation.reSpecial0 }),
      year: ValidationRules._date('CanHide', {
        utc_date: {
          dmy: false,
          my: false,
          y: true,
          range: [{ min: 'applicant.personIdentity.birthDate.value', up_val: true, err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
        },
      }),
    }),
    contractObligations: ValidationRules._hideable_object({
      value: ValidationRules._enum('AlwaysVisible', '0', '1'),
      organization: ValidationRules._string('CanHide', { max_length: 4096 }, { like: Validation.reSpecial0 }),
      year: ValidationRules._date('CanHide', {
        utc_date: {
          dmy: false,
          my: false,
          y: true,
          range: [{ min: 'applicant.personIdentity.birthDate.value', up_val: true, err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
        },
      }),
    }),
    militaryService: ValidationRules._enum('CanHide', '0', '1'),
    convicted: ValidationRules._enum('CanHide', '0', '1'),
    accused: ValidationRules._enum('CanHide', '0', '1'),
    legalObligations: ValidationRules._enum('CanHide', '0', '1'),
    bankrupt: ValidationRules._enum('CanHide', '0', '1'),
    mailing: ValidationRules._hideable_object({
      active: ValidationRules._bool('AlwaysVisible', 'false', 'true'),
      addressRus: ValidationRules._string('CanHide', { max_length: 1024 }, { like: Validation.reExtended }),
      addressLat: ValidationRules._string('CanHide', { max_length: 1024 }, { like: Validation.reExtended }),
    }),
    citizenshipCertificate: ValidationRules._hideable_object({
      active: ValidationRules._bool('AlwaysVisible', 'false', 'true'),
      number: ValidationRules._string('CanHide', { max_length: 255 }, { like: Validation.reSpecial2 }),
      date: ValidationRules._date('CanHide', {
        utc_date: {
          dmy: true,
          my: false,
          y: false,
          range: [
            { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
            { min: 'today', offset_edge: { y: -1 }, err: 'DOCUMENT_AGE_GREATER_THAN_1' },
            { max: 'today' },
          ],
        },
      }),
      officerPost: ValidationRules._string('CanHide', { max_length: 255 }, { like: Validation.reSpecial1 }),
      officerName: ValidationRules._string('CanHide', { max_length: 255 }, { like: Validation.reSpecial1 }),
      testimony: ValidationRules._string('CanHide', { max_length: 4096 }, { like: Validation.reCommon }),
    }),
    adultSpecialConditions: ValidationRules._enum('CanHide', '0', '1', '2', '3'),
    teenagerSpecialConditions: ValidationRules._enum('CanHide', '0', '1', '2'),
    presidentEdict: ValidationRules._hideable_object({
      number: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
      date: ValidationRules._date('AlwaysVisible', {
        utc_date: {
          dmy: true,
          my: false,
          y: false,
          range: [
            { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
            { min: 'today', offset_edge: { m: -3 }, err: 'DOCUMENT_AGE_GREATER_THAN_3' },
            { max: 'today' },
          ],
        },
      }),
    }),
    citizenshipDecision: ValidationRules._hideable_object({
      number: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
      date: ValidationRules._date('AlwaysVisible', {
        utc_date: {
          dmy: true,
          my: false,
          y: false,
          range: [
            { min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' },
            { min: 'today', offset_edge: { m: -3 }, err: 'DOCUMENT_AGE_GREATER_THAN_3' },
            { max: 'today' },
          ],
        },
      }),
      officerPost: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reOfficerPost }),
      officerName: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reSpecial1 }),
      article: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
    }),
    soldierPermission: ValidationRules._hideable_object({
      number: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reAnyDocumentNumber }),
      date: ValidationRules._date('AlwaysVisible', {
        utc_date: {
          dmy: true,
          my: false,
          y: false,
          range: [{ min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
        },
      }),
      signerPost: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
      signerName: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCyrillicFullNameWeak }),
      agreementNumber: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
      agreementDate: ValidationRules._date('AlwaysVisible', {
        utc_date: {
          dmy: true,
          my: false,
          y: false,
          range: [{ min: 'applicant.personIdentity.birthDate.value', err: 'LESS_THAN_BIRTHDATE' }, { max: 'today' }],
        },
      }),
      deciderName: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCyrillicFullNameWeak }),
      militaryUnit: ValidationRules._string('AlwaysVisible', { max_length: 255 }, { like: Validation.reCommon }),
    }),
    fingerprintsRequired: ValidationRules._enum('CanHide', '0', '1'),
    fingerprintsPresent: ValidationRules._bool('CanHide', 'false', 'true'),
    receptionUser: ValidationRules._ostring(
      'CanHide',
      { max_length: 255 },
      { like: Validation.reReceptionUser },
      { restrictions_for_common_passsport_type: { passportType: 'desiredForeignPassportType' } },
    ),
    receptionDate: ValidationRules._date('AlwaysVisible', { utc_date: { dmy: true, my: false, y: false, range: [] } }),
    inputMedia: ValidationRules._enum('AlwaysVisible', '0', '1', '2', '3', '4', '5'),
    mobileOptions: ValidationRules._hideable_object({
      department: ValidationRules._string('AlwaysVisible'),
      departmentUsesPassportMail: ValidationRules._bool('AlwaysVisible', 'false', 'true'),
      departmentUsesInternetApps: ValidationRules._bool('AlwaysVisible', 'false', 'true'),
      sendAppViaInternet: ValidationRules._bool('CanHide', 'false', 'true'),
      sendAppViaInternetWithFIO: ValidationRules._bool('CanHide', 'false', 'true'),
    }),
  };
}

// Export
export class UserModelValidation {
  constructor() {
    Features.registerValidationSpecification(
      RequestFeatures,
      Validation.rules,
      Validation.spec,
      Validation.warnings,
      Validation.notifications,
    );
    Features.registerValidationSpecification(
      RequestFeaturesV2,
      Validation.rules,
      Validation.spec2,
      Validation.warnings,
      Validation.notifications,
    );
  }
}
export class UserModelValidationMobile {
  constructor() {
    Features.registerValidationSpecification(
      RequestFeatures,
      Validation.rules,
      Validation.spec,
      Validation.warnings,
      Validation.notificationsMobile,
    );
    Features.registerValidationSpecification(
      RequestFeaturesV2,
      Validation.rules,
      Validation.spec2,
      Validation.warnings,
      Validation.notificationsMobile,
    );
  }
}
